/**
 * Visual material picker. Stella raises this when the customer
 * needs to choose a material/style. No prices on the cards by
 * design — the structured estimate reveals price after selection.
 */

import type { OptionsCardPayload } from "@starz/shared/api/types.gen";

interface Props {
  payload: OptionsCardPayload;
  selectedOptionId: string | null;
  onSelect: (option: OptionsCardPayload["cards"][number]) => void;
  disabled?: boolean;
}

export function OptionsCardGrid({ payload, selectedOptionId, onSelect, disabled = false }: Props) {
  const groupName = payload.group_name ?? payload.cards[0]?.option_group_name ?? "Pick one";
  return (
    <section
      aria-label={groupName}
      className="space-y-3 rounded-xl border border-starz-gold/20 bg-starz-cream/30 p-4"
    >
      <p className="text-xs font-semibold uppercase tracking-widest text-starz-gold">{groupName}</p>
      <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {payload.cards.map((c) => {
          const isSelected = c.option_id === selectedOptionId;
          return (
            <li key={c.option_id}>
              <button
                type="button"
                onClick={() => onSelect(c)}
                disabled={disabled}
                aria-pressed={isSelected}
                className={
                  "flex w-full flex-col gap-2 rounded-xl border-2 bg-white p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 " +
                  (isSelected
                    ? "border-starz-gold ring-2 ring-starz-gold/40"
                    : "border-starz-gold/15 hover:border-starz-gold/40")
                }
              >
                {c.photo_url ? (
                  <img
                    src={c.photo_url}
                    alt={c.display_name}
                    loading="lazy"
                    className="aspect-[4/3] w-full rounded-md object-cover"
                  />
                ) : (
                  <div className="grid aspect-[4/3] w-full place-items-center rounded-md bg-starz-cream text-xs text-warm-gray">
                    No photo yet
                  </div>
                )}
                <div>
                  <div className="text-base font-medium text-starz-navy">{c.display_name}</div>
                  {c.description && (
                    <div className="line-clamp-2 text-xs text-warm-gray">{c.description}</div>
                  )}
                </div>
                {isSelected && (
                  <span className="text-xs font-semibold uppercase tracking-widest text-starz-gold">
                    Selected
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
